import { CurrentUser } from '../../../lib/currentUser'

import { AuthCurrentResponse, ChargePointUser, EntitlementsContext, ScopedAccessContextStartResponse } from './web-api'

type AuthCurrentCachePatch = {
    user?: Partial<CurrentUser> | null
    selectedOrganizationId?: string | null
    entitlements?: EntitlementsContext | null
    portalAudience?: string | null
    scopedAccess?: ScopedAccessContextStartResponse | null
}

// Preserve the current cached field unless the patch explicitly replaces or clears it.
const resolvePatchedField = <T>(currentValue: T | null | undefined, nextValue: T | null | undefined): T | null => {
    if (typeof nextValue === 'undefined') {
        return currentValue ?? null
    }

    return nextValue
}

// Merge partial user updates into the cached auth/current user while still allowing explicit clear.
const mergePatchedUser = (
    currentUser: ChargePointUser | null | undefined,
    nextUser: Partial<CurrentUser> | null | undefined
): ChargePointUser | null => {
    if (typeof nextUser === 'undefined') {
        return currentUser ?? null
    }

    if (nextUser === null) {
        return null
    }

    return {
        ...(currentUser ?? {}),
        ...nextUser,
    }
}

// Apply a partial auth/current patch without losing cached fields that were not part of the update.
export const mergeAuthCurrentCache = (
    current: AuthCurrentResponse | void | undefined,
    patch: AuthCurrentCachePatch
): AuthCurrentResponse => ({
    user: mergePatchedUser(current?.user, patch.user),
    selectedOrganizationId: resolvePatchedField(current?.selectedOrganizationId, patch.selectedOrganizationId),
    entitlements: resolvePatchedField(current?.entitlements, patch.entitlements),
    portalAudience: resolvePatchedField(current?.portalAudience, patch.portalAudience),
    scopedAccess: resolvePatchedField(current?.scopedAccess, patch.scopedAccess),
})
